import { useLazyQuery } from "@apollo/client";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { GridInputs, SearchForm } from "../../../../components/Functional";
import { Button, Spinner } from "../../../../components/ui";
import {
  FETCH_CONTINENTS,
  FETCH_COUNTRIES,
  FETCH_SAGECODES,
} from "../../../../graphql/query/Country";
import { FETCH_COUNTIES } from "../../../../graphql/query/County";
import { FETCH_CURRENCIES } from "../../../../graphql/query/Currency";
import { FETCH_INVOICES_LIST as fetchInvoicesListQuery } from "../../../../graphql/query/Invoice";
import { FETCH_INVOICE_TYPES } from "../../../../graphql/query/InvoiceType";
import {
  FETCH_TAX_AREAS,
  FETCH_TAX_AREA_SAGE_CODES,
} from "../../../../graphql/query/TaxArea";
import { useCompaniesLazy } from "../../../../hooks/useCompanies";
import { useEntitiesLazy } from "../../../../hooks/useEntities";
import useUser from "../../../../hooks/useUser";
import { sanitizeReferenceFilters } from "../../../../util/parseInvoiceReference";
import { invoiceFilterStore, useInvoiceFilters } from "../useInvoiceFilters";
import AdvisorSale from "./components/AdvisorSale";
import Costs from "./components/Costs";
import Invoice from "./components/Invoice";
import Query from "./components/Query";
import Registrar from "./components/Registrar";
import Sort from "./components/Sort";
import Tabs from "./components/Tabs";
import Transaction from "./components/Transaction";
import { withExportedItemFilter } from "./exportedItemFilter";

const InvoiceSearch = props => {
  const { hasPermission } = useUser();
  const { closeModal, isSearchOpen = false, onSearchSubmitted } = props || {};
  const [fetchInvoicesV2, { loading, data: { invoicesV2 } = {} }] =
    useLazyQuery(fetchInvoicesListQuery());

  const [
    fetchContinentsLazily,
    { loading: continentsLoading, data: { continents = [] } = {} },
  ] = useLazyQuery(FETCH_CONTINENTS);
  const [
    fetchCountriesLazily,
    { loading: countriesLoading, data: { countries = [] } = {} },
  ] = useLazyQuery(FETCH_COUNTRIES);
  const [
    fetchCurrenciesLazily,
    { loading: currenciesLoading, data: { currencies = [] } = {} },
  ] = useLazyQuery(FETCH_CURRENCIES);
  const [fetchEntitiesLazily, { loading: entitiesLoading, entities }] =
    useEntitiesLazy();
  const [
    fetchInvoiceTypesLazily,
    { loading: invoiceTypesLoading, data: { invoiceTypes = [] } = {} },
  ] = useLazyQuery(FETCH_INVOICE_TYPES);
  const [
    fetchSageCodesLazily,
    { loading: sageCodesLoading, data: { sagecodes = [] } = {} },
  ] = useLazyQuery(FETCH_SAGECODES);
  const [
    fetchtaxAreasLazily,
    { loading: taxAreasLoading, data: { taxAreas = [] } = {} },
  ] = useLazyQuery(FETCH_TAX_AREAS);
  const [
    fetchtaxAreaSageCodesLazily,
    { loading: taxAreaSageCodesLoading, data: { taxAreaSageCodes = [] } = {} },
  ] = useLazyQuery(FETCH_TAX_AREA_SAGE_CODES);
  const [
    fetchCountiesLazily,
    { loading: countiesLoading, data: { counties = [] } = {} },
  ] = useLazyQuery(FETCH_COUNTIES);
  const [fetchCompanies, { loading: companiesLoading, companies = [] }] =
    useCompaniesLazy({
      variables: {
        input: {
          sort1: { value: "name", operator: "DESC" },
        },
      },
    });

  useEffect(() => {
    if (isSearchOpen) {
      fetchContinentsLazily();
      fetchCountriesLazily();
      fetchCurrenciesLazily();
      fetchEntitiesLazily();
      fetchInvoiceTypesLazily();
      fetchSageCodesLazily();
      fetchtaxAreasLazily();
      fetchtaxAreaSageCodesLazily();
      fetchCountiesLazily();
      fetchCompanies();
    }
  }, [isSearchOpen]);

  const navigate = useNavigate();
  const location = useLocation();
  const { formik } = useInvoiceFilters();
  const input = invoiceFilterStore.get();

  const inputs = {
    className: "mt-5 grid grid-cols-1 gap-4 lg:grid-cols-4 lg:gap-16",
    inputs: [
      {
        className: "gap-4",
        inputs: [
          {
            hasOperator: true,
            isMulti: true,
            label: "Invoiced From",
            name: "companyId",
            options: companies?.edges?.map(item => ({
              label: `${item?.invoiceCode ? item?.invoiceCode + " |" : ""} ${item?.name || ""}`,
              value: item.id,
            })),
            type: "multi-select",
          },
          {
            allowEmpty: true,
            label: "Invoice #",
            name: "number",
            type: "text",
            placeholder: "WCA123456 or 123456",
          },
          {
            allowEmpty: true,
            label: "Proforma #",
            name: "proforma",
            type: "text",
            placeholder: "WCA123456 or 123456",
          },
          { label: "Date From", name: "date.value.from", type: "date" },
          { label: "Date To", name: "date.value.to", type: "date" },
          {
            label: "Payment Due From",
            name: "dueDate.value.from",
            type: "date",
          },
          { label: "Payment Due To", name: "dueDate.value.to", type: "date" },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          { allowEmpty: true, label: "Id", name: "id", type: "number" },
          {
            hasOperator: true,
            label: "Client",
            name: "contactId",
            type: "contactSearch",
          },
          { label: "Client Name", name: "contactName", type: "text" },
          {
            label: "Region",
            name: "_country.sagecode",
            type: "multi-select",
            options: sagecodes?.map(item => ({
              label: item?.sagecode,
              value: item?.sagecode,
            })),
          },
          {
            label: "Continent",
            name: "_country.continent",
            type: "multi-select",
            options: continents?.map(item => ({
              label: item?.continent,
              value: item?.continent,
            })),
          },
          {
            label: "Country",
            name: "countryId",
            type: "multi-select",
            options: countries?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
          },
          {
            label: "State / County",
            name: "county",
            type: "multi-select",
            options: counties?.map(item => ({
              label: `${item?.code} - ${item?.description}`,
              value: item?.code,
            })),
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            label: "Sales Person",
            name: "_invoiceSalesPerson.salesUserId",
            options: entities?.edges?.map(item => ({
              label: item.name,
              value: item.id,
            })),
            type: "multi-select",
          },
          {
            label: "Split",
            name: "_invoiceSalesPerson.split",
            type: "number-max-min",
            step: 0.25,
            min: 0,
            max: 1,
            hasOperator: true,
            operatorOptions: [
              { label: "Equal To", value: "eq" },
              { label: "Less Than", value: "lte" },
            ],
          },
          { label: "Analysis Code", name: "sagecode", type: "text" },
          {
            label: "Type",
            name: "typeId",
            options: invoiceTypes?.map(item => ({
              label: item.description,
              value: item.id,
            })),
            type: "multi-select",
          },
          {
            label: "Currency",
            name: "currencyId",
            options: currencies?.map(currency => ({
              label: `${currency.description} (${currency.code})`,
              value: currency.id,
            })),
            type: "multi-select",
          },
          {
            label: "Tax Area",
            name: "taxareaId",
            options: taxAreas?.map(item => ({
              label: item?.name,
              value: item?.id,
            })),
            type: "multi-select",
          },
          {
            label: "Tax Code",
            name: "_taxArea.sageCode",
            options: taxAreaSageCodes?.map(item => ({
              label: item?.sageCode,
              value: item?.id,
            })),
            type: "multi-select",
          },
        ],
      },
      {
        className: "grid-cols-1 gap-4",
        inputs: [
          {
            designType: "threeStateCheckbox",
            label: "Approved",
            name: "approved",
            type: "toggle",
            disabled: !hasPermission("UPDATE_INVOICE_SALES_NOTES"),
          },
          {
            designType: "threeStateCheckbox",
            label: "Charge Storage",
            name: "chargeStorage",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "New Business",
            name: "newBusiness",
            type: "toggle",
          },
          {
            designType: "threeStateCheckbox",
            label: "Exported",
            name: "exported",
            type: "toggle",
          },
        ],
      },
    ],
  };

  const inputProps = {
    formik,
    ...inputs,
  };

  const handleSubmit = e => {
    e?.preventDefault();
    const currentParams = new URLSearchParams(location.search);
    const nextParams = new URLSearchParams({ status: "all" });
    ["listId", "imageListId", "workingListId", "imageWorkingListId"].forEach(
      key => {
        const value = currentParams.get(key);
        if (value) {
          nextParams.set(key, value);
        }
      },
    );

    // Users can type a full reference like "WCA123456" into Invoice # /
    // Proforma #. The DB columns are integers and the "WCA" prefix is the
    // company's invoiceCode, so we strip it before sending and use it to
    // scope the search by companyId. The cleaned filter is written back to
    // the store so the searched-invoices table sees the same shape.
    //
    // The Exported toggle is UI-only (-1/0/null); we fold it into the
    // server-shape input here rather than mirroring it into Formik on
    // every keystroke, so all "form value -> request shape" translation
    // happens in one place.
    const sanitizedInput = withExportedItemFilter(
      sanitizeReferenceFilters(input, companies?.edges || []),
      formik?.values?.exported,
    );
    invoiceFilterStore.set(sanitizedInput);

    fetchInvoicesV2({
      variables: {
        input: sanitizedInput,
      },
    }).then(() => {
      navigate(`/invoice?${nextParams.toString()}`);
      // Performing a new search invalidates any selection from the previous
      // result set; clear it so stale ids cannot reach bulk actions.
      onSearchSubmitted?.();
      closeModal();
    });
  };

  const searchButtonProps = {
    action: "black",
    label: loading ? "Searching" : "Search",
    disabled: loading,
    type: "submit",
  };

  const resetButtonProps = {
    action: "default",
    label: "Reset",
    onClick: formik?.resetForm,
  };

  const tabComponentProps = {
    invoicesV2,
    formik,
  };

  const tabsProps = {
    formik,
    name: "tab",
    options: [
      {
        label: "Invoice",
        value: "invoice",
        component: <Invoice {...tabComponentProps} />,
      },
      {
        label: "Transactions",
        value: "transactions",
        component: <Transaction {...tabComponentProps} />,
      },
      {
        label: "Registrar",
        value: "registrar",
        component: <Registrar {...tabComponentProps} />,
      },
      {
        label: "Advisor Sale",
        value: "advisorSale",
        component: <AdvisorSale {...tabComponentProps} />,
      },
      {
        label: "Costs",
        value: "costs",
        component: <Costs {...tabComponentProps} />,
      },
      {
        label: "Sort",
        value: "sort",
        component: <Sort {...tabComponentProps} />,
      },
      {
        label: "Query",
        value: "query",
        component: <Query {...tabComponentProps} />,
      },
    ],
  };

  return (
    <SearchForm onSubmit={handleSubmit}>
      <div className="flex flex-col">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-xl font-bold">Invoice Search</div>
          <div className="flex flex-wrap gap-4">
            <Button {...resetButtonProps} />
            <Button {...searchButtonProps} />
          </div>
        </div>
        {continentsLoading ||
        countriesLoading ||
        currenciesLoading ||
        entitiesLoading ||
        invoiceTypesLoading ||
        sageCodesLoading ||
        taxAreasLoading ||
        taxAreaSageCodesLoading ||
        countiesLoading ||
        companiesLoading ? (
          <div className="flex h-[70dvh] w-full items-center justify-center ">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            <GridInputs {...inputProps} />
            <Tabs {...tabsProps} />
          </div>
        )}
      </div>
    </SearchForm>
  );
};

export default InvoiceSearch;
